<template>
  <woot-modal :show="show" :on-close="onClose">
    <woot-modal-header
      :header-title="isEditing ? $t('BUSINESS_DATA.WEB_SOURCES.EDIT') : $t('BUSINESS_DATA.WEB_SOURCES.ADD_NEW')"
    />
    <form
      class="flex flex-col modal-content"
      @submit.prevent="handleSubmit"
    >
      <div class="space-y-5">
          <div class="space-y-2">
            <woot-input
              v-model="form.name"
              :label="$t('BUSINESS_DATA.WEB_SOURCES.NAME')"
              :required="true"
              :placeholder="$t('BUSINESS_DATA.WEB_SOURCES.NAME_PLACEHOLDER')"
              :error="v$.form.name.$error ? $t('BUSINESS_DATA.WEB_SOURCES.NAME_ERROR') : ''"
              :class="{ error: v$.form.name.$error }"
              type="text"
              maxlength="100"
              @blur="v$.form.name.$touch"
            />
          </div>

          <div class="space-y-2">
            <woot-input
              v-model="form.url"
              :label="$t('BUSINESS_DATA.WEB_SOURCES.URL')"
              :required="true"
              :placeholder="$t('BUSINESS_DATA.WEB_SOURCES.URL_PLACEHOLDER')"
              :error="v$.form.url.$error ? (v$.form.url.required.$invalid ? $t('BUSINESS_DATA.WEB_SOURCES.URL_ERROR') : $t('BUSINESS_DATA.WEB_SOURCES.URL_INVALID')) : ''"
              :class="{ error: v$.form.url.$error }"
              type="url"
              maxlength="500"
              @blur="v$.form.url.$touch"
            />
          </div>

          <div class="space-y-2">
            <FormLabel
              :label="$t('BUSINESS_DATA.WEB_SOURCES.CONTENT_TYPE')"
              :required="true"
              :has-error="v$.form.content_type.$error"
            />
            <select
              v-model="form.content_type"
              required
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:!border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors duration-200"
              :class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500/20 dark:border-red-400 dark:focus:border-red-400': v$.form.content_type.$error }"
              @blur="v$.form.content_type.$touch"
            >
              <option value="website">{{ $t('BUSINESS_DATA.WEB_SOURCES.URL') }}</option>
              <option value="documentation">{{ $t('BUSINESS_DATA.WEB_SOURCES.SITEMAP') }}</option>
              <option value="blog">{{ $t('BUSINESS_DATA.WEB_SOURCES.RSS') }}</option>
            </select>
            <span v-if="v$.form.content_type.$error" class="text-xs text-red-600 dark:text-red-400">
              {{ $t('BUSINESS_DATA.WEB_SOURCES.CONTENT_TYPE_ERROR') }}
            </span>
          </div>

          <div v-if="isEditing" class="space-y-2">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('BUSINESS_DATA.WEB_SOURCES.STATUS') }}
            </label>
            <select
              v-model="form.status"
              class="w-full px-3 py-2 text-sm border border-slate-200 dark:!border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors duration-200"
            >
              <option value="pending">{{ $t('BUSINESS_DATA.WEB_SOURCES.PENDING') }}</option>
              <option value="active">{{ $t('BUSINESS_DATA.WEB_SOURCES.ACTIVE') }}</option>
              <option value="paused">{{ $t('BUSINESS_DATA.WEB_SOURCES.PAUSED') }}</option>
              <option value="error">{{ $t('BUSINESS_DATA.WEB_SOURCES.ERROR') }}</option>
            </select>
          </div>

          <div class="space-y-4">
            <h3 class="text-sm font-medium text-slate-700 dark:text-slate-300">
              {{ $t('BUSINESS_DATA.WEB_SOURCES.CRAWL_CONFIG') }}
            </h3>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div class="space-y-2">
                <label class="block text-xs font-medium text-slate-600 dark:text-slate-400">
                  {{ $t('BUSINESS_DATA.WEB_SOURCES.MAX_PAGES') }}
                </label>
                <input
                  v-model.number="uiCrawl.max_pages"
                  type="number"
                  min="1"
                  class="w-full px-3 py-2 text-sm rounded-lg transition-colors duration-200"
                  placeholder="20"
                />
              </div>
              <div class="space-y-2">
                <label class="block text-xs font-medium text-slate-600 dark:text-slate-400">
                  {{ $t('BUSINESS_DATA.WEB_SOURCES.MAX_DEPTH') }}
                </label>
                <input
                  v-model.number="uiCrawl.max_depth"
                  type="number"
                  min="0"
                  class="w-full px-3 py-2 text-sm rounded-lg transition-colors duration-200"
                  placeholder="2"
                />
              </div>
              <div class="space-y-2">
                <label class="block text-xs font-medium text-slate-600 dark:text-slate-400">
                  {{ $t('BUSINESS_DATA.WEB_SOURCES.DELAY') }}
                </label>
                <input
                  v-model.number="uiCrawl.delay"
                  type="number"
                  step="0.1"
                  min="0"
                  class="w-full px-3 py-2 text-sm rounded-lg transition-colors duration-200"
                  placeholder="0.5"
                />
              </div>
            </div>
          </div>
      </div>

      <div class="flex justify-center gap-3 pt-4">
        <woot-button
          type="button"
          variant="smooth"
          color-scheme="ghost"
          @click="onClose"
        >
          {{ $t('COMMON.CANCEL') }}
        </woot-button>

        <woot-button
          type="submit"
          variant="smooth"
          color-scheme="success"
          :is-loading="isSubmitting"
          :disabled="!isFormValid"
        >
          {{ isEditing ? $t('COMMON.UPDATE') : $t('COMMON.CREATE') }}
        </woot-button>
      </div>
    </form>
  </woot-modal>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import FormLabel from 'dashboard/components/widgets/forms/FormLabel.vue';

export default {
  name: 'WebContentSourceModal',
  components: { FormLabel },
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    source: {
      type: Object,
      default: null
    }
  },
  
  emits: ['close', 'saved'],
  
  setup() {
    return { v$: useVuelidate() };
  },
  
  data() {
    return {
      isSubmitting: false,
      form: {
        name: '',
        url: '',
        content_type: 'website',
        status: 'pending',
        crawl_config: {}
      },
      crawlConfigText: '',
      uiCrawl: { max_pages: 20, max_depth: 2, delay: 0 }
    };
  },
  
  validations() {
    return {
      form: {
        name: { required },
        url: { required, url },
        content_type: { required },
      },
    };
  },
  
  computed: {
    isEditing() {
      return !!this.source;
    },
    
    isFormValid() {
      return !this.v$.$invalid &&
             this.form.name.length <= 100 &&
             this.form.url.length <= 500;
    }
  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
        if (this.source) {
          this.populateForm();
        }
      }
    },
    
    source: {
      handler(newVal) {
        if (newVal && this.show) {
          this.populateForm();
        }
      },
      immediate: true
    }
  },
  
  methods: {
    resetForm() {
      this.form = {
        name: '',
        url: '',
        content_type: 'website',
        status: 'pending',
        crawl_config: {}
      };
      this.crawlConfigText = '';
      this.uiCrawl = { max_pages: 20, max_depth: 2, delay: 0 };
      this.v$.$reset();
    },
    
    populateForm() {
      if (this.source) {
        this.form = {
          name: this.source.name || '',
          url: this.source.url || '',
          content_type: this.source.content_type || 'website',
          status: this.source.status || 'pending',
          crawl_config: this.source.crawl_config || {}
        };
        const cfg = this.form.crawl_config || {};
        this.uiCrawl = {
          max_pages: Number(cfg.max_pages || 20),
          max_depth: Number(cfg.max_depth || 2),
          delay: Number(cfg.delay || 0)
        };
        this.crawlConfigText = JSON.stringify(cfg, null, 2);
      }
    },
    
    async handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid || !this.isFormValid) {
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        // Build crawl config from UI fields
        const crawlConfig = {
          max_pages: this.uiCrawl.max_pages || 20,
          max_depth: this.uiCrawl.max_depth || 2,
          delay: this.uiCrawl.delay || 0
        };
        
        const payload = {
          ...this.form,
          crawl_config: crawlConfig
        };
        
        const accountId = this.$store.getters.getCurrentAccountId;
        let response;
        
        if (this.isEditing) {
          response = await window.axios.put(
            `/api/v1/accounts/${accountId}/web_content_sources/${this.source.id}`,
            { web_content_source: payload }
          );
        } else {
          response = await window.axios.post(
            `/api/v1/accounts/${accountId}/web_content_sources`,
            { web_content_source: payload }
          );
        }
        
        this.$emit('saved', response.data.web_content_source);
        
      } catch (error) {
        console.error('Error saving web content source:', error);
        if (error.response?.data?.errors) {
          useAlert(error.response.data.errors.join(', '), { type: 'error' });
        } else {
          useAlert(this.$t('BUSINESS_DATA.WEB_SOURCES.SAVE_ERROR'), { type: 'error' });
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    
    onClose() {
      this.$emit('close');
    }
  }
};
</script>
